// frontend/src/App.tsx
import type { ReactNode } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

// Estilos de la barra lateral (Color Admin)
import './components/sidebar-collapsed.css';
import './components/sidebar-fix.css';

// Páginas públicas
import Login from './pages/Login';
import Register from './pages/Register';

// Protección (ruta protegida para el espacio Devioz)
import ProtectedRouteDevioz from './routes/ProtectedRouteDevioz';

// Devioz (Color Admin)
import DeviozLayout from './pages/devioz/DeviozLayout';
import DeviozDashboard from './pages/devioz/DeviozDashboard';
import Inicio from './pages/devioz/Inicio';
import Perfil from './pages/devioz/Perfil';
import Configuracion from './pages/devioz/Configuracion';
import Companies from './pages/devioz/Companies';
import Users from './pages/devioz/Users';
import Roles from './pages/devioz/Roles';

// Auth helpers (opcional; si no lo tienes, puedes quitarlo y
// dejar que PublicOnlyRoute valide sólo por token en storage)
import { isAuthenticated } from './auth';

const Unauthorized = () => (
  <div className="container py-5 text-center">
    <h2 className="mb-2">403 – No autorizado</h2>
    <p>No tienes permisos para acceder a esta sección.</p>
    <a className="btn btn-dark mt-3" href="/">Ir al inicio</a>
  </div>
);

/**
 * Rutas públicas que solo deben mostrarse si NO hay sesión.
 * Si hay sesión válida, redirige a la ruta previa (state.from) o a /devioz.
 */
function PublicOnlyRoute({ children }: { children: ReactNode }) {
  const location = useLocation();
  if (typeof isAuthenticated === 'function' && isAuthenticated()) {
    const from = (location.state as any)?.from?.pathname || '/devioz';
    return <Navigate to={from} replace />;
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      {/* Públicas (envueltas en PublicOnlyRoute) */}
      <Route
        path="/"
        element={
          <PublicOnlyRoute>
            <Login />
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicOnlyRoute>
            <Register />
          </PublicOnlyRoute>
        }
      />

      {/* Compatibilidad: dashboard antiguo -> nuevo dashboard */}
      <Route path="/dashboard" element={<Navigate to="/devioz/dashboard" replace />} />

      {/* Sección Devioz con layout Color Admin (protegida) */}
      <Route
        path="/devioz/*"
        element={
          <ProtectedRouteDevioz
            // Si quieres exigir pertenencia a la compañía Devioz, activa esto:
            // requireDeviozCompany
          >
            <DeviozLayout />
          </ProtectedRouteDevioz>
        }
      >
        {/* Index del segmento /devioz */}
        <Route index element={<Navigate to="inicio" replace />} />

        {/* Rutas internas */}
        <Route path="inicio" element={<Inicio />} />
        <Route path="dashboard" element={<DeviozDashboard />} />
        <Route path="empresas" element={<Companies />} />
        <Route path="usuarios" element={<Users />} />
        <Route path="roles" element={<Roles />} />

        {/* NUEVAS rutas */}
        <Route path="perfil" element={<Perfil />} />
        <Route path="configuracion" element={<Configuracion />} />

        {/* 404 dentro del layout */}
        <Route
          path="*"
          element={
            <div className="container py-5 text-center">
              <h2 className="mb-2">404 – Página no encontrada</h2>
              <p>La ruta que intentas abrir no existe.</p>
              <a className="btn btn-outline-dark mt-3" href="/devioz/dashboard">
                Ir al dashboard
              </a>
            </div>
          }
        />
      </Route>

      {/* Aliases útiles */}
      <Route path="/companies" element={<Navigate to="/devioz/empresas" replace />} />
      <Route path="/users" element={<Navigate to="/devioz/usuarios" replace />} />
      <Route path="/roles" element={<Navigate to="/devioz/roles" replace />} />

      {/* 403 */}
      <Route path="/unauthorized" element={<Unauthorized />} />

      {/* 404 global */}
      <Route
        path="*"
        element={
          <div className="container py-5 text-center">
            <h2 className="mb-2">404 – Página no encontrada</h2>
            <p>La ruta que intentas abrir no existe.</p>
            <a className="btn btn-outline-dark mt-3" href="/devioz/dashboard">
              Ir al dashboard
            </a>
          </div>
        }
      />
    </Routes>
  );
}
